import { USER_LEVEL } from '~/constants';
import {
  ACCREDIT_STATUS,
  USER_LEVEL_KEY,
  type AccreditInfo,
  type AuthThirdRequest,
  type ChangePhoneRequestData,
  type CompanyInfoData,
  type CreateStarterOrgData,
  type CreateStarterOrgResponse,
  type CreateTasteOrgData,
  type CreateTasteOrgResponse,
  type EditTeamMemberAccount,
  type HaierLoginRequest,
  type InvoiceOrder,
  type LoginEmailRequestData,
  type LoginInfo,
  type Member,
  type Permission,
  type PgcFiveView,
  type PgcImageCheckResult,
  type PgcRecord,
  type PgcSubmitParams,
  type PublicKeyData,
  type RegisterEmailRequestData,
  type Session,
  type TeamMemberAccount,
  type UserAwardInfo,
  // LoginRequestData,
  type UserInfo,
  type UserInvoiceInfo,
  type UTMRequestData,
} from '../interface';
import type {
  IPageResponse,
  IPaginatedConfig,
  IResponse,
  RequestConfig,
} from '../interface/common';
import type { IOrder } from '../interface/order';
import { fetch } from '../request';
import {
  withRegisterOrLoginExtraParams,
  type IWechatOfficialAccountScanStatus,
  type IWechatOfficialAccountTicket,
} from './open-login';
import type { IOrderResult } from './rights';

// 获取手机验证码
export const getRegisterCode = (data: { mobile: string }, config?: RequestConfig) => {
  return fetch<IResponse>('/user/mobile/send_sms_code/', {
    method: 'POST',
    body: data,
    ...config,
  });
};

// 登录
// export const login = (data: LoginRequestData, config?: RequestConfig) => {
//   return fetch<IResponse<LoginInfo[]>>("/user/mobile/complex_register_login/", {
//     method: "POST",
//     body: data,
//     // noToken: true,
//     ...config,
//   });
// };

// 手机验证码登录
export const loginByPhone = (
  data: {
    mobile: string;
    sms_code: string;
    source?: string;
    from_ppt_activity?: number;
    channel_code?: string;
  },
  config?: RequestConfig
) => {
  return fetch<IResponse<LoginInfo[]>>('/user/account30/sms_complex_register_login/', {
    method: 'POST',
    body: withRegisterOrLoginExtraParams(data),
    // noToken: true,
    ...config,
  });
};

// 获取信息脱敏公共key
export const getPublicKey = (config?: RequestConfig) => {
  return fetch<IResponse<PublicKeyData>>('/user/rsa/get_public_key/', {
    method: 'GET',
    ...config,
  });
};

interface AccountLoginData extends Partial<UTMRequestData> {
  rsa_key_id: number;
  auth_data: string;
  source?: string;
}

// 账号登录
export const loginByAccount = async (data: AccountLoginData, config?: RequestConfig) => {
  const response = await fetch<IResponse<LoginInfo[]>>('/user/account30/password_complex_login/', {
    method: 'POST',
    body: withRegisterOrLoginExtraParams(data),
    ...config,
  });
  return response;
};

// 获取用户详细信息
export const getUserInfo = () => {
  return fetch<IResponse<UserInfo>>('/user/account/info/', {
    method: 'GET',
  }).then(res => {
    if (res.data && import.meta.client) {
      const val = res.data;
      const isPerson = [
        USER_LEVEL_KEY.TASTE,
        USER_LEVEL_KEY.PERSONAL,
        USER_LEVEL_KEY.PERSONAL_LITE,
      ].includes(val?.rights_type);

      sensorsLogin(res.data.user_id);

      // sensorsSetProfile(res.data)

      trackEvent(TRACK_EVENT.YOUYAN_USER_PROPERTY, {
        ID: val?.user_id,
        PersonalUsername: val?.login_name,
        PersonalNickname: val?.account_name,
        IsTeamAdmin: isPerson ? 0 : 1,
        TeamID: isPerson ? undefined : val?.organization_id,
        RegisterTime: val?.create_time,
      });
      if (!isPerson) {
        trackEvent(TRACK_EVENT.YOUYAN_TEAM_PROPERTY, {
          TeamID: val?.organization_id,
          TeamName: val?.organization_name,
          TeamRight: USER_LEVEL()[val.rights_type]?.name,
          TeamNum: val?.rights?.sub_account_num,
          CreateTime: val?.organization_create_time,
          Token:
            val?.account_remained_credit_coin +
            val?.current_buy_more_remained_credit_coin +
            (val?.free_account_remained_credit_coin ? val?.free_account_remained_credit_coin : 0),
        });
      }
    }
    return res;
  });
};

// 获取用户手机号账号信息
export const getUserAccountList = () => {
  return fetch<IResponse<LoginInfo[]>>('/user/account30/account_list/', {
    method: 'POST',
  });
};

// 通过用户ID获取登录token信息
export const getUserToken = (user_id: number) => {
  return fetch<IResponse>('/user/account30/switch_login/', {
    method: 'POST',
    body: {
      user_id,
    },
  });
};

// 记录登录用户log
export const setUserLog = () => {
  return fetch<IResponse>('/logs/login_logs/', {
    method: 'POST',
  });
};

export interface IGetOrganizationAccounts extends Partial<IPaginatedConfig> {
  name_or_mobile?: boolean;
  ignore_kicked: boolean;
  is_active_user: number | 0 | 1 | 2; // 0:禁用，1：启用，2:全部
}
// 用户列表/搜索
export const getMembers = (params?: IGetOrganizationAccounts) => {
  return fetch<IResponse<Member[]>>('/user/account/orginzation_account/', {
    method: 'GET',
    params,
  });
};

// ]团队管理员恢复人
export const restoreMembers = (body?: { user_id: string }) => {
  return fetch('/user/account/enable_account/', {
    method: 'PUT',
    body,
  });
};

// 恢复被踢出的账号
export const reinviteMembers = (body?: { user_id: string }) => {
  return fetch('/user/account/invite_account/', {
    method: 'PUT',
    body,
  });
};

// ]团队管理员踢人
export const kickMembers = (body?: any) => {
  return fetch('/user/account30/kick/', {
    method: 'POST',
    body,
  });
};

// 消费记录
export const consumption = (params?: any) => {
  return fetch('/order/consumption/client/', {
    method: 'GET',
    params,
  });
};
// 消费记录name
export const consumptionName = (params?: any) => {
  return fetch('/order/consumption/client/project_names/', {
    method: 'GET',
    params,
  });
};

// ]创建邀请码
export const createInvitation = (body?: any) => {
  return fetch('/user/account30/gen_invitation_token/', {
    method: 'POST',
    body,
  });
};

// 查看邀请码信息
export const lookCodeInfo = (params?: { token: string }) => {
  return fetch('/user/account30/invitation_token_info/', {
    method: 'GET',
    params,
  });
};

// 接受邀请
export const invitation = (body?: any, config?: RequestConfig) => {
  return fetch('/user/account30/invitation/', {
    method: 'POST',
    body,
    ...config,
  });
};

// 订单列表
export const orderList = (params?: any, config?: RequestConfig) => {
  return fetch<IPageResponse<IOrderResult>>('/order/client/', {
    method: 'GET',
    params,
    ...config,
  });
};

// 可开票列表
export const invoiceList = (params?: { no_in: string }) => {
  return fetch<IPageResponse<IOrder>>('/order/invoice/orders/', {
    method: 'GET',
    params,
  });
};

// 获取开票信息默认值
export const invoiceUserInfo = (params?: { order_id: string }) => {
  return fetch<IResponse<UserInvoiceInfo[]>>('/order/invoice/last_filled/', {
    method: 'GET',
    params,
  });
};

// 申请开票
export const applyInvoice = (data: UserInvoiceInfo) => {
  return fetch<IResponse>('/order/invoice/', {
    method: 'POST',
    body: data,
  });
};

// 已开票列表
export const invoiceOrders = (params?: any) => {
  return fetch<IPageResponse<InvoiceOrder[]>>('/order/invoice/', {
    method: 'GET',
    params,
  });
};

// 更换手机号
export const changePhone = (data: ChangePhoneRequestData) => {
  return fetch<IResponse>('/user/account30/change_mobile/', {
    method: 'POST',
    body: data,
  });
};

// 修改账号名称
export const changeAccountName = (data: { login_name: string }) => {
  return fetch<IResponse>('/user/account30/change_login_name/', {
    method: 'POST',
    body: data,
  });
};

// 更换头像/昵称
export const updateUserInfo = (data: { account_name?: string; logo_path?: string }) => {
  return fetch<IResponse>('/user/account/update_info/', {
    method: 'POST',
    body: data,
  });
};

// 更换密码-已登录
export const changePasswordLogin = (data: {
  rsa_key_id: number;
  auth_data: string;
  mobile: string;
  sms_code: string;
}) => {
  return fetch<IResponse>('/user/account30/change_password/', {
    method: 'POST',
    body: data,
  });
};

// 更换密码-未登录
export const changePassword = (data: {
  rsa_key_id: number;
  auth_data: string;
  mobile: string;
  sms_code: string;
}) => {
  return fetch<IResponse>('/user/account30/reset_password/', {
    method: 'POST',
    body: data,
  });
};

// 修改团队名称
export const changeTeamName = (data: { name: string }) => {
  return fetch<IResponse>('/user/org/update/', {
    method: 'POST',
    body: data,
  });
};

// 转让管理员
export const changeTeamAdmin = (data: {
  account_id: number;
  sms_code: string;
  mobile?: string;
  email?: string;
}) => {
  return fetch<IResponse>('/user/account30/change_org_admin_account/', {
    method: 'POST',
    body: data,
  });
};

// 企业主页账号信息获取
export const getCompanyInfo = (params: { id: string }) => {
  return fetch<IResponse<CompanyInfoData>>('/user/org/homepage_info/', {
    method: 'GET',
    params,
  });
};

// 注销账号验证码校验
export const checkDeleteCode = (data: { sms_code: string; mobile: string }) => {
  console.debug(data, 377);
  return fetch<IResponse>('/user/account30/check_sms_code/', {
    method: 'POST',
    body: data,
  });
};

// 注销账号
export const deleteAccount = (data: { sms_code: string }) => {
  return fetch<IResponse>('/user/account30/unregister/', {
    method: 'POST',
    body: data,
  });
};
// 检查验证码是否可用
export function checkSmsCode(data: { code: string }) {
  return fetch<IResponse>('/activity/code/using/', {
    method: 'POST',
    body: data,
  });
}
// 更新团队Logo
export function reqUpdateLogo(data: { platform_logo_path: string; organization_id: number }) {
  return fetch<IResponse>('/user/org/change_logo/', {
    method: 'PUT',
    body: data,
  });
}

// 判断新用户是否有活动奖励
export const getUserAward = (config?: RequestConfig) => {
  return fetch<IResponse<UserAwardInfo>>('/user/login_pop_up_info/', {
    method: 'GET',
    ...config,
  });
};

// 创建企业体验版账号
export const createTasteOrg = (data: CreateTasteOrgData) =>
  fetch<IResponse<CreateTasteOrgResponse>>('/user/account30/user_create_org/', {
    method: 'POST',
    body: data,
  });

export const createStarterOrg = (data: CreateStarterOrgData) =>
  fetch<IResponse<CreateStarterOrgResponse>>('/user/account30/user_create_tmp_org/', {
    method: 'POST',
    body: data,
  });

// 注销团队账号
export const deleteTeamAccount = (data: { org_id?: number }) => {
  return fetch<IResponse>('/user/org/unregister/', {
    method: 'POST',
    body: data,
  });
};

// 邮箱登录
export const loginByEmail = (data: LoginEmailRequestData, config?: RequestConfig) => {
  return fetch<IResponse<LoginInfo[]>>('/user/international/login_by_email/', {
    method: 'POST',
    body: data,
    // noToken: true,
    ...config,
  });
};

// 邮箱注册
export const registerByEmail = (data: RegisterEmailRequestData, config?: RequestConfig) => {
  return fetch<IResponse<LoginInfo[]>>('/user/international/register_login_by_email/', {
    method: 'POST',
    body: data,
    // noToken: true,
    ...config,
  });
};

// 邮箱验证码
export const getEmailCode = (data: { email: string }, config?: RequestConfig) => {
  return fetch<IResponse>('/user/international/send_code_by_email/', {
    method: 'POST',
    body: data,
    ...config,
  });
};

// 邮箱重置密码
export const changePasswordEmail = (data: {
  rsa_key_id: number;
  auth_data: string;
  email: string;
  sms_code: string;
}) => {
  return fetch<IResponse>('/user/international/reset_password_by_email/', {
    method: 'POST',
    body: data,
  });
};

// 更换邮箱信息
export const changeEmail = (data: { email: string; verify_code: string }) => {
  return fetch<IResponse>('/user/international/change_email/', {
    method: 'POST',
    body: data,
  });
};

// oauth2 登录授权信息
interface IGetUserInternationalLoginAuthorizeParams {
  service: 'google'; // oauth 服务方
}
interface ILoginAuthorizeInfo {
  nonce: string;
  url: string;
  state: string;
  client_id: string;
  scope: string;
}
export const getUserInternationalLoginAuthorize = (
  params: IGetUserInternationalLoginAuthorizeParams
) => {
  return fetch<IResponse<ILoginAuthorizeInfo>>('/user/international/oauth2_authorize/', {
    method: 'GET',
    params,
    credential: false,
  });
};

interface IPostUserInternationalOauth2LoginBody extends UTMRequestData {
  code: string;
  state: string;
  service: 'google';
  source?: string;
  link_code?: string;
  country: string;
}
export const postUserInternationalOauth2Login = (body: IPostUserInternationalOauth2LoginBody) => {
  return fetch<IResponse<LoginInfo[]>>('/user/international/oauth2_login/', {
    method: 'POST',
    body,
  });
};

export const putUserOrgUpdateOrgLogoOrName = (
  data: Partial<{
    logo_path: string;
    org_id: number;
    name: string;
  }>
) => {
  return fetch<IResponse>('/user/org/update_org_logo_or_name/', {
    method: 'PUT',
    body: data,
  });
};

export const putUserOrgBackgroundCover = (
  data: Partial<{
    org_id: number;
    background_cover_path: string;
  }>
) => {
  return fetch<IResponse>('/user/org/background_cover/', {
    method: 'PUT',
    body: data,
  });
};

// 转授权签约
export const putAccreditResult = (
  data: Partial<{
    refuse_reason?: string;
    status: ACCREDIT_STATUS;
    id: number;
  }>
) => {
  return fetch<IResponse>('/user/template_authorization_record/', {
    method: 'PUT',
    body: data,
  });
};

// 获取授权详情
export const getAccreditResult = (params: { id: number }) => {
  return fetch<IResponse<AccreditInfo[]>>('/user/template_authorization_record/', {
    method: 'GET',
    params,
  });
};

// 用户获取pgc记录
export const getUserPgcRecord = (params: { token: string }) => {
  return fetch<IResponse<PgcRecord>>('/order/pgc_record/', {
    method: 'GET',
    params,
  });
};

// 用户提交pgc审核
export const postUserPgcRecord = (data: { id: number }) => {
  return fetch<IResponse<PgcSubmitParams>>('/order/pgc_record/submit/', {
    method: 'POST',
    body: data,
  });
};

// 用户撤回pgc审核
export const cancelUserPgcRecord = (token: string) => {
  return fetch<IResponse<PgcRecord>>('/order/pgc_record/revoke/', {
    method: 'POST',
    body: { token },
  });
};

// 用户修改已提交的pgc审核
export const updateUserPgcRecord = (data: PgcSubmitParams) => {
  return fetch<IResponse<PgcRecord>>('/order/pgc_record/edit/', {
    method: 'PUT',
    body: data,
  });
};

// 判断pgc订单是否有效
export const checkPgcOrder = (params: { token: string }) => {
  return fetch<IResponse>('/order/pgc_record/test_token/', {
    method: 'GET',
    params,
  });
};

// 获取pgc五试图案例
export const getPgcFiveView = () => {
  return fetch<IResponse<PgcFiveView[]>>('/app_cfg/platform/pgc_banner/', {
    method: 'GET',
    // params,
  });
};

// 获取pgc用户是否首次打开
export const getPgcFirstOpen = (params: { user_id: number }) => {
  return fetch<IResponse>('/user/user_pop_up_memory/', {
    method: 'GET',
    params,
  });
};

// 标识pgc用户首次打开
export const putPgcFirstOpen = (data: { user_id: number; config: any; id: number }) => {
  return fetch<IResponse>(`/user/user_pop_up_memory/?id=${data.id}`, {
    method: 'PUT',
    body: data,
  });
};
// 用户未打开过标识
export const postPgcFirstOpen = (data: {
  user_id: number;
  config: any;
  // id: number
}) => {
  return fetch<IResponse>('/user/user_pop_up_memory/', {
    method: 'POST',
    body: data,
  });
};

// pgc图片检测提交
export const postPgcImageCheck = (data: { image_urls: string[] }) => {
  return fetch<
    IResponse<{
      task_id: string;
    }>
  >('/media/media_moderation_task/', {
    method: 'POST',
    body: data,
  });
};

// 检测pgc图片检测结果
export const getPgcImageCheckResult = (params: { task_ids: string[] }) => {
  return fetch<IResponse<PgcImageCheckResult[]>>('/media/media_moderation_task/status/', {
    method: 'POST',
    body: params,
  });
};

// 私有化和纯净版的权限数据
export const getPermissionData = () => {
  return fetch<IResponse<Permission>>('/app_cfg/platform/', {
    method: 'GET',
  });
};

// 解绑飞书
export const postUnbindFeiShu = () => {
  return fetch('/user/account30/unbind_feishu/', {
    method: 'POST',
  });
};

// 飞书回调
export const getFeiShuCallback = (params: any) => {
  return fetch('/open_login/feishu/callback/', {
    method: 'GET',
    params,
  });
};

// 绑定手机号
export const postFeishuAccountCreate = (data: {
  mobile: string;
  code: string;
  sms_code: string;
}) => {
  return fetch('/open_login/feishu/feishu_account_create/', {
    method: 'POST',
    body: data,
  });
};

// 解绑微信
export const postUnbindWechat = () => {
  return fetch('/user/account30/unbind_wechat/', {
    method: 'POST',
  });
};

// 获取微信绑定状态
export const getBindWechatStatus = (ticket_id: string, config?: RequestConfig) => {
  return fetch<IResponse<IWechatOfficialAccountScanStatus>>('/user/account30/bind_wechat_status/', {
    method: 'GET',
    params: {
      ticket_id,
    },
    ...config,
  });
};

// 获取绑定微信的二维码
export const postBindWechat = () => {
  return fetch<IResponse<IWechatOfficialAccountTicket>>('/user/account30/bind_wechat/', {
    method: 'POST',
  });
};

export const createTeamMember = (data: TeamMemberAccount) =>
  fetch<IResponse>('/user/account30/create_account/', {
    method: 'POST',
    body: data,
  });

export const editTeamMember = (data: EditTeamMemberAccount) =>
  fetch('/user/account30/update_account_info/', { method: 'PUT', body: data });

export const haierLogin = (data: HaierLoginRequest) =>
  fetch('/haier/login/', { method: 'POST', body: data });

export const authThird = (data: AuthThirdRequest, config: RequestConfig) =>
  fetch<IResponse<Session>>('/open_login/agency/login/', {
    method: 'POST',
    body: data,
    ...config,
  });

export const loginForWeb = (data: Session) => {
  return fetch('/api/session', {
    method: 'POST',
    body: data,
    baseURL: location.origin,
  });
};

export const logoutForWeb = () => {
  return fetch('/api/session', {
    method: 'DELETE',
    baseURL: location.origin,
  });
};

export const logoutForBe = () => {
  return fetch('/user/session/', {
    method: 'DELETE',
  });
};
